import { getEngine } from './ui/engineClient';

// =========================================================================
// 大局将棋 Canvas UI — Web Worker バックエンド版 (updated)
// =========================================================================

const CELL_SIZE = 72;
const BOARD_SIZE = 36;
const MARGIN = 60;
const CANVAS_WIDTH = CELL_SIZE * BOARD_SIZE + MARGIN * 2;
const CANVAS_HEIGHT = CELL_SIZE * BOARD_SIZE + MARGIN * 2;

let engine: any = null;
let boardData: any = null;      // {pieces: [...], current_turn, board_x, board_y, can_undo}

const PIECE_PATH = new Path2D(`
M 0 -32.4
L 24.48 -19.44
L 30.6 32.4
L -30.6 32.4
L -24.48 -19.44
Z
`);

let selectedPid: number | null = null;
let selectedSquare: {x: number, y: number} | null = null;
let validMoves: any[] = [];
let highlightedPids: number[] = [];
let maxPieceValue = 1;
let aiThinking = false;
let selectedIsOpponent = false;

// --- 特殊動作状態 ---
let shishiMidMove: any = null;
let shishiSecondMoves: any[] = [];
let honjuuState: any = null;
let honjuuNextMoves: any[] = [];
let lastMoveInfo: any = null;

let canvas: HTMLCanvasElement;
let ctx: CanvasRenderingContext2D;
let statusMsg: HTMLElement;
let turnIndicator: HTMLElement;

async function init() {
    canvas = document.getElementById('shogi-board') as HTMLCanvasElement;
    ctx = canvas.getContext('2d')!;
    statusMsg = document.getElementById('status-message')!;
    turnIndicator = document.getElementById('turn-indicator')!;

    try {
        window.addEventListener('resize', scaleCanvas);
        document.getElementById('cb-fit-height')!.addEventListener('change', scaleCanvas);
        
        statusMsg.innerText = "エンジン初期化中...";
        engine = await getEngine();

        statusMsg.innerText = "盤面データ読み込み中...";
        await fetchBoard();
        
        setupCanvas();
        renderBoard();
        attachEventListeners();

        document.getElementById('cb-value-shade')!.addEventListener('change', renderBoard);
        document.getElementById('cb-type-color')!.addEventListener('change', (e: any) => {
            document.getElementById('color-legend')!.style.display = e.target.checked ? 'flex' : 'none';
            renderBoard();
        });
        document.getElementById('cb-show-arrow')!.addEventListener('change', renderBoard);
        document.getElementById('cb-flip-board')!.addEventListener('change', renderBoard);
        document.getElementById('cb-focus-lines')!.addEventListener('change', renderBoard);
        document.getElementById('inp-focus-alpha')!.addEventListener('input', renderBoard);

        const autoFlipCheck = () => {
            const sente = (document.getElementById('sel-sente') as HTMLSelectElement).value;
            const gote = (document.getElementById('sel-gote') as HTMLSelectElement).value;
            const cbFlip = document.getElementById('cb-flip-board') as HTMLInputElement;
            if (sente === 'ai' && gote === 'human') cbFlip.checked = true;
            else if (sente === 'human' && gote === 'ai') cbFlip.checked = false;
            renderBoard();
        };

        document.getElementById('sel-sente')!.addEventListener('change', () => { autoFlipCheck(); checkAndTriggerAI(); });
        document.getElementById('sel-gote')!.addEventListener('change', () => { autoFlipCheck(); checkAndTriggerAI(); });
        
        autoFlipCheck();
        statusMsg.innerText = "準備完了: 駒を選択して動かすことができます。";
        statusMsg.style.color = "#166534";
        
        setupPieceSearch();

        checkAndTriggerAI();

    } catch (err: any) {
        console.error(err);
        if (statusMsg) {
            statusMsg.innerText = "データの読み込みに失敗しました: " + err.message;
            statusMsg.style.color = "#b91c1c";
        }
    }
}

async function fetchBoard() {
    boardData = await engine.getBoardState();
    updateTurnIndicator();
    maxPieceValue = 1;

    // TODO: pieces配列がない場合のモック対応
    if (!boardData.pieces) boardData.pieces = [];

    boardData.pieces.forEach((p: any) => {
        if (p.ability !== 'gyoku' && p.value > maxPieceValue) maxPieceValue = p.value;
    });
}

function updateTurnIndicator() {
    if (!boardData) return;
    const turn = boardData.current_turn === 0 ? '先手' : '後手';
    turnIndicator.innerText = `手番: ${turn}`;
    turnIndicator.style.color = boardData.current_turn === 0 ? '#1d4ed8' : '#dc2626';
}

function setupCanvas() {
    let dpr = window.devicePixelRatio || 1;
    // スマートフォンの厳しいメモリ制限(OOM)を回避するため、画面幅に応じてCanvasの最大解像度を下げる
    const isMobile = window.innerWidth <= 800;
    const maxDimension = isMobile ? 2048 : 4096;
    const maxDpr = maxDimension / Math.max(CANVAS_WIDTH, CANVAS_HEIGHT);
    if (dpr > maxDpr) {
        dpr = maxDpr;
    }

    canvas.width = CANVAS_WIDTH * dpr;
    canvas.height = CANVAS_HEIGHT * dpr;
    ctx.scale(dpr, dpr);
    scaleCanvas();
}

function scaleCanvas() {
    const availableWidth = window.innerWidth;
    const availableHeight = window.innerHeight;
    const header = document.getElementById('main-header');
    const headerHeight = header && !header.classList.contains('collapsed') ? header.offsetHeight : 0;
    const usableHeight = availableHeight - headerHeight;
    const fitHeightChecked = (document.getElementById('cb-fit-height') as HTMLInputElement).checked;
    
    if (fitHeightChecked) {
        const scale = usableHeight / CANVAS_HEIGHT;
        canvas.style.height = `${usableHeight}px`;
        canvas.style.width = `${CANVAS_WIDTH * scale}px`;
        canvas.style.margin = '0 auto';
        canvas.style.display = 'block';
    } else {
        const scale = Math.max(0.1, availableWidth / CANVAS_WIDTH);
        canvas.style.width = `100vw`;
        canvas.style.height = `${CANVAS_HEIGHT * scale}px`;
        canvas.style.margin = '0';
        canvas.style.display = 'block';
    }
}

// =========================================================================
// Global Hooks for HTML Inline Handlers
// =========================================================================

(window as any).toggleHeader = () => {
    const header = document.getElementById('main-header');
    const btn = document.getElementById('btn-header-toggle');
    if (header && btn) {
        header.classList.toggle('collapsed');
        btn.innerText = header.classList.contains('collapsed') ? '展開🔽' : '最小化🔼';
        setTimeout(() => { scaleCanvas(); renderBoard(); }, 300);
    }
};

(window as any).doUndo = async () => {
    try {
        await engine.undoMove();
        await fetchBoard();
        resetSelection();
        statusMsg.innerText = "1手戻しました。";
        statusMsg.style.color = "#166534";
        renderBoard();
    } catch (err) {
        statusMsg.innerText = "Undo に失敗しました。";
        statusMsg.style.color = "#b91c1c";
    }
};

(window as any).doReset = async () => {
    if (!confirm("盤面を初期状態にリセットしますか？")) return;
    try {
        await engine.resetBoard();
        await fetchBoard();
        resetSelection();
        lastMoveInfo = null;
        statusMsg.innerText = "盤面をリセットしました。";
        statusMsg.style.color = "#166534";
        renderBoard();
    } catch (err) {
        statusMsg.innerText = "リセットに失敗しました。";
        statusMsg.style.color = "#b91c1c";
    }
};

function resetSelection() {
    selectedPid = null;
    selectedSquare = null;
    validMoves = [];
    shishiMidMove = null;
    shishiSecondMoves = [];
    honjuuState = null;
    honjuuNextMoves = [];
}

/**
 * [u2ffX-uXXXX-uXXXX] 表記を、対応するTTFフォントで「〓」を描画する
 * <span> に変換する。TTFフォントは U+3013 にカスタムグリフを持つため、
 * font-family を切り替えて「〓」を表示すると正しい字形が出る。
 */
function formatPieceNameHTML(name: string): string {
    if (!name) return name;
    return name.replace(/\[u([0-9a-f]{4})-u([0-9a-f]{4})-u([0-9a-f]{4})\]/g, (_match: string, p1: string, p2: string, p3: string) => {
        const key = `u${p1}-u${p2}-u${p3}`;
        return `<span style="font-family:'Piece-${key}',serif;">\u3013</span>`;
    });
}

// =========================================================================
// Rendering Helpers
// =========================================================================
function getRenderXY(x: number, y: number) {
    const isFlipped = (document.getElementById('cb-flip-board') as HTMLInputElement)?.checked;
    if (isFlipped) {
        return { rx: BOARD_SIZE - 1 - x, ry: BOARD_SIZE - 1 - y };
    }
    return { rx: x, ry: y };
}

// =========================================================================
// Rendering 
// =========================================================================
function renderBoard() {
    if (!boardData) return;
    
    // Background
    const boardGrad = ctx.createLinearGradient(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    boardGrad.addColorStop(0, '#f7d08a');
    boardGrad.addColorStop(1, '#e6b96a');
    ctx.fillStyle = boardGrad;
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Grid
    ctx.strokeStyle = 'rgba(20, 10, 0, 0.7)';
    ctx.lineWidth = 1.2;
    for (let i = 0; i <= BOARD_SIZE; i++) {
        const pos = MARGIN + i * CELL_SIZE;
        ctx.beginPath(); ctx.moveTo(pos, MARGIN); ctx.lineTo(pos, MARGIN + BOARD_SIZE * CELL_SIZE); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(MARGIN, pos); ctx.lineTo(MARGIN + BOARD_SIZE * CELL_SIZE, pos); ctx.stroke();
    }

    // Stars
    const stars = [6, 12, 18, 24, 30];
    ctx.fillStyle = '#1a0d00';
    stars.forEach(x => {
        stars.forEach(y => {
            ctx.beginPath();
            ctx.arc(MARGIN + x * CELL_SIZE, MARGIN + y * CELL_SIZE, 5, 0, Math.PI * 2);
            ctx.fill();
        });
    });

    // 直前に動かした駒の枠ハイライト
    if (lastMoveInfo) {
        const rFrom = getRenderXY(lastMoveInfo.to_x, lastMoveInfo.to_y);
        const hx = MARGIN + rFrom.rx * CELL_SIZE;
        const hy = MARGIN + rFrom.ry * CELL_SIZE;
        ctx.strokeStyle = 'rgba(239, 68, 68, 0.9)'; // Red border
        ctx.lineWidth = 4;
        ctx.strokeRect(hx, hy, CELL_SIZE, CELL_SIZE);
    }

    // 検索ハイライト（マゼンタ + 枠）
    if (highlightedPids.length > 0) {
        const highlightSet = new Set(highlightedPids);
        boardData.pieces.forEach((p: any) => {
            if (highlightSet.has(p.pid)) {
                const rPos = getRenderXY(p.x, p.y);
                const hx = MARGIN + rPos.rx * CELL_SIZE;
                const hy = MARGIN + rPos.ry * CELL_SIZE;
                ctx.fillStyle = 'rgba(217, 70, 239, 0.35)';
                ctx.fillRect(hx, hy, CELL_SIZE, CELL_SIZE);
                ctx.strokeStyle = 'rgba(168, 34, 195, 0.9)';
                ctx.lineWidth = 3;
                ctx.strokeRect(hx + 1.5, hy + 1.5, CELL_SIZE - 3, CELL_SIZE - 3);
            }
        });
    }

    // 選択中のマスのハイライト
    if (selectedSquare) {
        const rSel = getRenderXY(selectedSquare.x, selectedSquare.y);
        ctx.fillStyle = 'rgba(59, 130, 246, 0.4)';
        ctx.fillRect(
            MARGIN + rSel.rx * CELL_SIZE,
            MARGIN + rSel.ry * CELL_SIZE,
            CELL_SIZE, CELL_SIZE
        );
    }

    // 味方サポートハイライト
    validMoves.forEach(move => {
        if (move.type === 'ally') {
            const rMove = getRenderXY(move.x, move.y);
            ctx.fillStyle = 'rgba(34, 197, 94, 0.4)';
            ctx.fillRect(MARGIN + rMove.rx * CELL_SIZE, MARGIN + rMove.ry * CELL_SIZE, CELL_SIZE, CELL_SIZE);
        }
    });

    // 駒描画
    // pieceMap で高速に (x,y) → piece を引けるようにする
    const pieceMap: Record<number, any> = {};
    boardData.pieces.forEach((p: any) => {
        pieceMap[p.y * BOARD_SIZE + p.x] = p;
    });

    for (let y = 0; y < BOARD_SIZE; y++) {
        for (let x = 0; x < BOARD_SIZE; x++) {
            const p = pieceMap[y * BOARD_SIZE + x];
            if (!p) continue;
            drawPiece(x, y, p.name, p.side === 1, p.is_promoted, p.value, p.special_move, p.ability);
        }
    }

    // 移動先ハイライト (駒の上に描画)
    validMoves.forEach(move => {
        if (['empty', 'enemy', 'shishi_step', 'shishi_jump', 'honjuu_step', 'honjuu_jump'].includes(move.type)) {
            const rMove = getRenderXY(move.x, move.y);
            ctx.fillStyle = selectedIsOpponent
                ? 'rgba(220, 38, 38, 0.25)'   // 相手の駒: 薄い赤丸
                : 'rgba(220, 38, 38, 0.7)';    // 自分の駒: 通常の赤丸
            ctx.beginPath();
            ctx.arc(
                MARGIN + rMove.rx * CELL_SIZE + CELL_SIZE / 2,
                MARGIN + rMove.ry * CELL_SIZE + CELL_SIZE / 2,
                CELL_SIZE * 0.25, 0, Math.PI * 2
            );
            ctx.fill();
        }
    });

    // 獅子2回目の移動候補表示
    shishiSecondMoves.forEach(move => {
        const rMove = getRenderXY(move.x, move.y);
        if (move.type === 'stay') {
            // 1回のみ移動（その場に留まる）: 緑色の丸
            ctx.fillStyle = 'rgba(34, 197, 94, 0.8)';
            ctx.beginPath();
            ctx.arc(
                MARGIN + rMove.rx * CELL_SIZE + CELL_SIZE / 2,
                MARGIN + rMove.ry * CELL_SIZE + CELL_SIZE / 2,
                CELL_SIZE * 0.25, 0, Math.PI * 2
            );
            ctx.fill();
        } else if (move.type === 'empty' || move.type === 'enemy') {
            ctx.fillStyle = 'rgba(220, 38, 38, 0.7)';
            ctx.beginPath();
            ctx.arc(
                MARGIN + rMove.rx * CELL_SIZE + CELL_SIZE / 2,
                MARGIN + rMove.ry * CELL_SIZE + CELL_SIZE / 2,
                CELL_SIZE * 0.25, 0, Math.PI * 2
            );
            ctx.fill();
        }
    });

    // 奮鵲のジャンプ連鎖候補表示
    honjuuNextMoves.forEach(move => {
        const rMove = getRenderXY(move.x, move.y);
        if (move.type === 'stay') {
            // 1回のみ移動（その場に留まる）: 緑色の丸
            ctx.fillStyle = 'rgba(34, 197, 94, 0.8)';
            ctx.beginPath();
            ctx.arc(
                MARGIN + rMove.rx * CELL_SIZE + CELL_SIZE / 2,
                MARGIN + rMove.ry * CELL_SIZE + CELL_SIZE / 2,
                CELL_SIZE * 0.25, 0, Math.PI * 2
            );
            ctx.fill();
        } else if (['empty', 'enemy', 'honjuu_step', 'honjuu_jump'].includes(move.type)) {
            ctx.fillStyle = 'rgba(220, 38, 38, 0.7)';
            ctx.beginPath();
            ctx.arc(
                MARGIN + rMove.rx * CELL_SIZE + CELL_SIZE / 2,
                MARGIN + rMove.ry * CELL_SIZE + CELL_SIZE / 2,
                CELL_SIZE * 0.25, 0, Math.PI * 2
            );
            ctx.fill();
        }
    });

    // 移動軌跡 (矢印)
    if (lastMoveInfo && (document.getElementById('cb-show-arrow') as HTMLInputElement)?.checked) {
        drawMoveArrow(lastMoveInfo);
    }

    // 集中線 (直前に動かした駒) - 他の駒の上に描画して目立たせる
    if (lastMoveInfo && (document.getElementById('cb-focus-lines') as HTMLInputElement)?.checked) {
        drawFocusLines(lastMoveInfo.to_x, lastMoveInfo.to_y);
    }
}

// --- 集中線描画 ---
function drawFocusLines(x: number, y: number) {
    const rPos = getRenderXY(x, y);
    const centerX = MARGIN + rPos.rx * CELL_SIZE + CELL_SIZE / 2;
    const centerY = MARGIN + rPos.ry * CELL_SIZE + CELL_SIZE / 2;
    const maxRadius = Math.max(CANVAS_WIDTH, CANVAS_HEIGHT) * 1.5;
    const minRadius = CELL_SIZE * 0.7;
    const lineCount = 120;
    const alpha = parseFloat((document.getElementById('inp-focus-alpha') as HTMLInputElement)?.value) || 0.7;
    
    let seed = x * 1000 + y * 7 + 12345;
    const random = () => {
        const val = Math.sin(seed++) * 10000;
        return val - Math.floor(val);
    };

    ctx.save();
    ctx.translate(centerX, centerY);
    
    ctx.beginPath();
    for (let i = 0; i < lineCount; i++) {
        const angle = (i / lineCount) * Math.PI * 2 + (random() * 0.05);
        const innerR = minRadius + random() * CELL_SIZE * 1.5;
        ctx.moveTo(Math.cos(angle) * innerR, Math.sin(angle) * innerR);
        ctx.lineTo(Math.cos(angle) * maxRadius, Math.sin(angle) * maxRadius);
    }
    ctx.strokeStyle = `rgba(255, 255, 255, ${alpha})`;
    ctx.lineWidth = 3;
    ctx.stroke();
    
    ctx.beginPath();
    for (let i = 0; i < 80; i++) {
        const angle = (i / 80) * Math.PI * 2 + (random() * 0.05);
        const innerR = minRadius + random() * CELL_SIZE * 0.5;
        ctx.moveTo(Math.cos(angle) * innerR, Math.sin(angle) * innerR);
        ctx.lineTo(Math.cos(angle) * maxRadius * 0.5, Math.sin(angle) * maxRadius * 0.5);
    }
    ctx.strokeStyle = `rgba(255, 255, 150, ${Math.min(1, alpha + 0.1)})`;
    ctx.lineWidth = 2;
    ctx.stroke();

    ctx.restore();
}

// --- 移動軌跡描画 ---
function drawMoveArrow(info: any) {
    ctx.save();
    ctx.strokeStyle = 'rgba(239, 68, 68, 0.6)';
    ctx.lineWidth = 6;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    const rFrom = getRenderXY(info.from_x, info.from_y);
    const startX = MARGIN + rFrom.rx * CELL_SIZE + CELL_SIZE / 2;
    const startY = MARGIN + rFrom.ry * CELL_SIZE + CELL_SIZE / 2;
    
    ctx.beginPath();
    ctx.moveTo(startX, startY);

    let lastPtX = startX;
    let lastPtY = startY;

    if (info.path && info.path.length > 0) {
        info.path.forEach((pt: any) => {
            const rMid = getRenderXY(pt.x, pt.y);
            const midX = MARGIN + rMid.rx * CELL_SIZE + CELL_SIZE / 2;
            const midY = MARGIN + rMid.ry * CELL_SIZE + CELL_SIZE / 2;
            ctx.lineTo(midX, midY);
            
            // 中間点マーカーを描画
            ctx.fillStyle = 'rgba(239, 68, 68, 0.8)';
            ctx.arc(midX, midY, 6, 0, Math.PI * 2);
            ctx.fill();
            ctx.moveTo(midX, midY); // continue line

            lastPtX = midX;
            lastPtY = midY;
        });
    }

    const rTo = getRenderXY(info.to_x, info.to_y);
    const endX = MARGIN + rTo.rx * CELL_SIZE + CELL_SIZE / 2;
    const endY = MARGIN + rTo.ry * CELL_SIZE + CELL_SIZE / 2;
    ctx.lineTo(endX, endY);
    ctx.stroke();

    // 矢印の頭
    const dx = endX - lastPtX;
    const dy = endY - lastPtY;
    const angle = Math.atan2(dy, dx);
    const headlen = 15;

    ctx.beginPath();
    ctx.moveTo(endX, endY);
    ctx.lineTo(endX - headlen * Math.cos(angle - Math.PI / 6), endY - headlen * Math.sin(angle - Math.PI / 6));
    ctx.lineTo(endX - headlen * Math.cos(angle + Math.PI / 6), endY - headlen * Math.sin(angle + Math.PI / 6));
    ctx.lineTo(endX, endY);
    ctx.fillStyle = 'rgba(239, 68, 68, 0.6)';
    ctx.fill();

    ctx.restore();
}

// --- 種別色ヘルパー ---
function getTypeColor(specialMove: number, ability: string | null) {
    if (ability === 'shishi' || ability === 'honjuu') return {r:0, g:128, b:255};   // #0080FF
    if (ability === 'kougyou' || ability === 'tengu' || ability === 'kujaku') return {r:255, g:0, b:64}; // #FF0040
    if (specialMove === 36) return {r:0, g:255, b:191};   // #00FFBF
    if (specialMove === 37) return {r:0, g:255, b:0};     // #00FF00
    if (specialMove >= 38 && specialMove <= 40) return {r:128, g:0, b:255}; // #8000FF
    return null; // デフォルト
}

function drawPiece(x: number, y: number, displayName: string, isOpponent: boolean, isPromoted = false,
                   pieceValue = 0, specialMove = 0, ability: string | null = null) {
    const rPos = getRenderXY(x, y);
    const centerX = MARGIN + rPos.rx * CELL_SIZE + CELL_SIZE / 2;
    const centerY = MARGIN + rPos.ry * CELL_SIZE + CELL_SIZE / 2;

    const showValueShade = (document.getElementById('cb-value-shade') as HTMLInputElement).checked;
    const showTypeColor = (document.getElementById('cb-type-color') as HTMLInputElement).checked;
    const isFlipped = (document.getElementById('cb-flip-board') as HTMLInputElement)?.checked;

    ctx.save();
    ctx.translate(centerX, centerY);
    
    // If the board is flipped, we reverse the concept of "opponent" so the pieces facing us are readable.
    const renderRotation = isFlipped ? !isOpponent : isOpponent;
    if (renderRotation) ctx.rotate(Math.PI);

    // 影
    ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
    ctx.shadowBlur = 5;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;

    // 駒形状
    const w = CELL_SIZE * 0.85;
    const h = CELL_SIZE * 0.9;
    const wHalf = w / 2;
    const hHalf = h / 2;

    // --- 駒の色を決定 ---
    let baseLight = [254, 245, 226];   // #fef5e2
    let baseMid   = [244, 228, 188];   // #f4e4bc
    let baseDark  = [222, 198, 138];   // #dec68a

    // 種別色分け: ベース色に色相を混ぜつつ知覚輝度を保つ
    if (showTypeColor) {
        const tc = getTypeColor(specialMove, ability);
        if (tc) {
            const mix = 0.55; // 混合率 (読みやすいよう濃いめ)
            const luma = (rgb: number[]) => 0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2];
            // 各ベース色を混合後、輝度を元に戻すようスケーリング
            [baseLight, baseMid, baseDark].forEach((base, idx) => {
                const origLuma = luma(base);
                const mixed = base.map((v, i) => v * (1 - mix) + [tc.r, tc.g, tc.b][i] * mix);
                const newLuma = luma(mixed);
                const scale = newLuma > 0 ? origLuma / newLuma : 1;
                const corrected = mixed.map(v => Math.min(255, Math.round(v * scale)));
                if (idx === 0) baseLight = corrected;
                else if (idx === 1) baseMid = corrected;
                else baseDark = corrected;
            });
        }
    }

    // 駒価値による濃淡: log(value) で 0~1 に正規化し、高価値ほど明るく
    if (showValueShade && pieceValue > 0 && ability !== 'gyoku') {
        const alpha = Math.log(pieceValue + 1) / Math.log(maxPieceValue + 1);
        const brightness = Math.min(1.0, 0.25 + alpha * 0.75); // 低価値=0.25(暗) → 高価値=1.0(明). 極端な値は1.0でクランプ
        baseLight = baseLight.map(v => Math.round(v * brightness));
        baseMid   = baseMid.map(v   => Math.round(v * brightness));
        baseDark  = baseDark.map(v   => Math.round(v * brightness));
    }

    // 木製グラデーション
    const pieceGrad = ctx.createLinearGradient(-wHalf, -hHalf, wHalf, hHalf);
    pieceGrad.addColorStop(0, `rgb(${baseLight[0]},${baseLight[1]},${baseLight[2]})`);
    pieceGrad.addColorStop(0.5, `rgb(${baseMid[0]},${baseMid[1]},${baseMid[2]})`);
    pieceGrad.addColorStop(1, `rgb(${baseDark[0]},${baseDark[1]},${baseDark[2]})`);
    ctx.fillStyle = pieceGrad;
    ctx.fill(PIECE_PATH);

    // 輪郭
    ctx.shadowColor = 'transparent';
    ctx.strokeStyle = '#3d2e16';
    ctx.lineWidth = 1.0;
    ctx.stroke(PIECE_PATH);

    // 文字
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const chars = displayName.match(/\[u[0-9a-f]{4}-u[0-9a-f]{4}-u[0-9a-f]{4}\]|./gu) || [];
    const fontSize = chars.length > 2 ? 18 : (chars.length === 2 ? 22 : 24);
    const baseFontStr = `"Hiragino Mincho ProN", "Yu Mincho", "MS Mincho", serif`;

    const drawChar = (charStr: string, ox: number, oy: number) => {
        if (charStr.startsWith('[')) {
            const key = charStr.slice(1, -1);
            ctx.font = `bold ${fontSize}px "Piece-${key}", ${baseFontStr}`;
            ctx.fillText('\u3013', ox, oy);
        } else {
            ctx.font = `bold ${fontSize}px ${baseFontStr}`;
            ctx.fillText(charStr, ox, oy);
        }
    };

    const drawText = (ox: number, oy: number, color: string) => {
        ctx.fillStyle = color;
        if (chars.length === 1) {
            drawChar(chars[0], ox, oy + (hHalf * 0.1));
        } else if (chars.length === 2) {
            drawChar(chars[0], ox, -hHalf * 0.32 + oy);
            drawChar(chars[1], ox, hHalf * 0.45 + oy);
        } else {
            const step = h / (chars.length + 1.2);
            chars.forEach((char, idx) => {
                drawChar(char, ox, -hHalf * 0.5 + (idx * step) + oy);
            });
        }
    };

    drawText(0.6, 0.6, 'rgba(0,0,0,0.15)');
    drawText(0, 0, isPromoted ? '#c22' : '#0f0800');

    ctx.restore();
}

// =========================================================================
// Input Handling
// =========================================================================

function attachEventListeners() {
    canvas.addEventListener('click', async (e) => {
        const rect = canvas.getBoundingClientRect();
        const scaleX = CANVAS_WIDTH / rect.width;
        const scaleY = CANVAS_HEIGHT / rect.height;
        // Add margins relative to standard mapping
        const xCanvas = (e.clientX - rect.left) * scaleX;
        const yCanvas = (e.clientY - rect.top) * scaleY;
        
        const rx = Math.floor((xCanvas - MARGIN) / CELL_SIZE);
        const ry = Math.floor((yCanvas - MARGIN) / CELL_SIZE);
        
        if (rx < 0 || rx >= BOARD_SIZE || ry < 0 || ry >= BOARD_SIZE) return;
        
        const isFlipped = (document.getElementById('cb-flip-board') as HTMLInputElement)?.checked;
        const x = isFlipped ? BOARD_SIZE - 1 - rx : rx;
        const y = isFlipped ? BOARD_SIZE - 1 - ry : ry;
        
        await handleSquareClick(x, y);
    });
}

function isCurrentTurnAI() {
    if (!boardData) return false;
    const turn = boardData.current_turn;
    if (turn === 0) return (document.getElementById('sel-sente') as HTMLSelectElement).value === 'ai';
    return (document.getElementById('sel-gote') as HTMLSelectElement).value === 'ai';
}

async function checkAndTriggerAI() {
    if (!isCurrentTurnAI() || aiThinking) return;
    aiThinking = true;
    const side = boardData.current_turn === 0 ? '先手' : '後手';
    statusMsg.innerText = `AI思考中... (${side})`;
    statusMsg.style.color = "#7c3aed";
    renderBoard();

    const depth = parseInt((document.getElementById('sel-depth') as HTMLSelectElement).value) || 1;
    const timeLimit = parseFloat((document.getElementById('inp-time') as HTMLInputElement).value) || 30;

    try {
        const res = await engine.aiMove(depth, timeLimit * 1000);
        aiThinking = false;

        if (res.status !== 'ok') {
            const loser = boardData.current_turn === 0 ? '先手' : '後手';
            const winner = boardData.current_turn === 0 ? '後手' : '先手';
            statusMsg.innerText = `${loser}(AI)に合法手がありません。${winner}の勝利！`;
            statusMsg.style.color = "#dc2626";
            (document.getElementById('sel-sente') as HTMLSelectElement).value = 'human';
            (document.getElementById('sel-gote') as HTMLSelectElement).value = 'human';
            renderBoard();
            return;
        }

        await fetchBoard();
        resetSelection();

        const mr = res.move_result;
        if (mr && mr.gyoku_loss) {
            statusMsg.innerText = `玉将/太子が全滅！ ${mr.gyoku_loss.winner}の勝利！`;
            statusMsg.style.color = "#dc2626";
            renderBoard();
            setTimeout(() => alert(`${mr.gyoku_loss.winner}の勝利です！`), 100);
            return;
        }

        const m = res.move;
        if (m.from_x != null && m.from_y != null) {
            lastMoveInfo = { pid: m.pid, from_x: m.from_x, from_y: m.from_y, to_x: m.to_x, to_y: m.to_y, path: m.path || [] };
        }

        statusMsg.innerText = `AI: (${m.to_x},${m.to_y})へ移動 | 評価=${res.score.toFixed(0)} 深さ=${res.depth_completed}`;
        statusMsg.style.color = "#166534";
        renderBoard();

        setTimeout(() => checkAndTriggerAI(), 200);

    } catch (err: any) {
        aiThinking = false;
        statusMsg.innerText = "AI探索に失敗しました: " + err.message;
        statusMsg.style.color = "#b91c1c";
    }
}

async function handleSquareClick(x: number, y: number) {
    if (aiThinking || isCurrentTurnAI()) return;
    
    // --- 奮鵲ジャンプ連鎖中の処理 ---
    if (honjuuState) {
        const nextIdx = honjuuNextMoves.findIndex(m => m.x === x && m.y === y);
        if (nextIdx !== -1) {
            const move = honjuuNextMoves[nextIdx];
            if (move.type === 'stay') {
                await executeHonjuuMove(honjuuState.pid, x, y, honjuuState.visited);
                return;
            }
            if (move.type === 'honjuu_jump' || move.type === 'enemy' || move.type === 'empty') {
                honjuuState.visited.push({x, y});
                await executeHonjuuMove(honjuuState.pid, x, y, honjuuState.visited);
                return;
            }
            if (move.type === 'honjuu_step') {
                honjuuState.visited.push({x, y});
                const newDistance = move.extra ? move.extra.new_distance : (honjuuState.current_distance + 1);
                honjuuState.remaining -= 1;
                honjuuState.current_distance = newDistance;
                try {
                    const res = await engine.honjuuNextMoves({
                        pid: honjuuState.pid,
                        direction: honjuuState.direction,
                        current_x: x, current_y: y,
                        remaining_steps: honjuuState.remaining,
                        origin_x: honjuuState.origin_x, origin_y: honjuuState.origin_y,
                        current_distance: newDistance,
                    });
                    if (res.status === 'ok') {
                        honjuuNextMoves = res.moves;
                        validMoves = [];
                        selectedSquare = { x, y };
                        statusMsg.innerText = `奔鷲の次の移動先を選択（残り${honjuuState.remaining}ステップ、緑丸=その場に留まる）`;
                        statusMsg.style.color = "#2563eb";
                        renderBoard();
                    }
                } catch (err) {
                    statusMsg.innerText = "奔鷲の次ステップ取得に失敗しました。";
                    statusMsg.style.color = "#b91c1c";
                }
                return;
            }
        }
        resetSelection();
        statusMsg.innerText = "奔鷲の移動をキャンセルしました。";
        statusMsg.style.color = "#57534e";
        renderBoard();
        return;
    }

    // --- 獅子2回目移動の処理 ---
    if (shishiMidMove) {
        const secondMoveIndex = shishiSecondMoves.findIndex(m => m.x === x && m.y === y);
        if (secondMoveIndex !== -1) {
            const move = shishiSecondMoves[secondMoveIndex];
            if (move.type === 'stay' || move.type === 'empty' || move.type === 'enemy') {
                await executeShishiMove(shishiMidMove.pid, shishiMidMove.mid_x, shishiMidMove.mid_y, x, y);
                return;
            }
        }
        resetSelection();
        statusMsg.innerText = "獅子の移動をキャンセルしました。";
        statusMsg.style.color = "#57534e";
        renderBoard();
        return;
    }

    const moveIndex = validMoves.findIndex(m => m.x === x && m.y === y);
    if (moveIndex !== -1 && selectedPid && !selectedIsOpponent) {
        const move = validMoves[moveIndex];
        if (move.type === 'empty' || move.type === 'enemy') {
            const midPos = (move.extra && move.extra.mid_pos) ? move.extra.mid_pos : null;
            await executeMove(selectedPid, x, y, move.capturedPath || [], midPos);
            return;
        }
        if (move.type === 'shishi_step') {
            try {
                const res = await engine.shishiSecondMoves({ pid: selectedPid, first_x: x, first_y: y });
                if (res) {
                    shishiMidMove = { pid: selectedPid, mid_x: x, mid_y: y };
                    shishiSecondMoves = res;
                    validMoves = [];  // 1回目の候補をクリア
                    selectedSquare = { x, y };  // 中間地点をハイライト
                    statusMsg.innerText = `獅子の2回目の移動先を選択してください（緑丸=その場に留まる）。`;
                    statusMsg.style.color = "#2563eb";
                    renderBoard();
                }
            } catch (err) {
                statusMsg.innerText = "獅子の2回目移動候補取得に失敗しました。";
                statusMsg.style.color = "#b91c1c";
            }
            return;
        }
        if (move.type === 'honjuu_step') {
            const extra = move.extra || {};
            const direction = extra.direction;
            const distance = extra.distance || 1;
            const maxSteps = extra.max_steps || 3;
            const remaining = maxSteps - distance;
            const piece = boardData.pieces.find((p: any) => p.pid === selectedPid);
            const originX = piece ? piece.x : x;
            const originY = piece ? piece.y : y;
            honjuuState = {
                pid: selectedPid,
                direction: direction,
                visited: [{x, y}],
                remaining: remaining,
                origin_x: originX,
                origin_y: originY,
                current_distance: distance,
            };
            try {
                const res = await engine.honjuuNextMoves({
                    pid: honjuuState.pid,
                    direction: honjuuState.direction,
                    current_x: x, current_y: y,
                    remaining_steps: honjuuState.remaining,
                    origin_x: originX, origin_y: originY,
                    current_distance: distance,
                });
                if (res.status === 'ok') {
                    honjuuNextMoves = res.moves;
                    validMoves = [];
                    selectedSquare = { x, y };
                    statusMsg.innerText = `奔鷲の次の移動先を選択（残り${honjuuState.remaining}ステップ、緑丸=その場に留まる）`;
                    statusMsg.style.color = "#2563eb";
                    renderBoard();
                }
            } catch (err) {
                statusMsg.innerText = "奔鷲の次ステップ候補取得に失敗しました。";
                statusMsg.style.color = "#b91c1c";
            }
            return;
        }
        if (move.type === 'honjuu_jump') {
            await executeHonjuuMove(selectedPid, x, y, [{x, y}]);
            return;
        }
        if (move.type === 'shishi_jump') {
            await executeMove(selectedPid, x, y, []);
            return;
        }
    }

    if (selectedSquare && selectedSquare.x === x && selectedSquare.y === y) {
        resetSelection();
        statusMsg.innerText = "選択を解除しました。";
        statusMsg.style.color = "#57534e";
        renderBoard();
        return;
    }

    resetSelection();

    const piece = boardData.pieces.find((p: any) => p.x === x && p.y === y);
    if (piece) {
        selectedPid = piece.pid;
        selectedSquare = { x, y };
        selectedIsOpponent = (piece.side !== boardData.current_turn);

        try {
            const res = await engine.calculateMoves(piece.pid);
            if (res) {
                validMoves = res;
                if (validMoves.length > 0) {
                    statusMsg.innerHTML = `${formatPieceNameHTML(piece.name)}を選択中: 移動先をクリックしてください。`;
                    statusMsg.style.color = "#2563eb";
                } else {
                    statusMsg.innerText = `動ける場所がありません。`;
                    statusMsg.style.color = "#b91c1c";
                }
            }
        } catch (err) {
            statusMsg.innerText = "Workerとの通信に失敗しました。";
            statusMsg.style.color = "#b91c1c";
        }
    } else {
        statusMsg.innerText = "空マスを選択しました。";
        statusMsg.style.color = "#57534e";
    }

    renderBoard();
}

async function executeMove(pid: number, toX: number, toY: number, capturedPath: any[] = [], midPos: any = null) {
    try {
        const pc = boardData.pieces.find((p: any) => p.pid === pid);
        if (pc) {
            const path = midPos ? [midPos] : [];
            lastMoveInfo = { pid, from_x: pc.x, from_y: pc.y, to_x: toX, to_y: toY, path: path };
        }

        const data = await engine.applyMove({ pid, to_x: toX, to_y: toY, captured_path: capturedPath });

        // 盤面を再取得
        await fetchBoard();
        resetSelection();

        if (data.gyoku_loss) {
            statusMsg.innerText = `玉将/太子が全滅！ ${data.gyoku_loss.winner}の勝利！`;
            statusMsg.style.color = "#dc2626";
            renderBoard();
            setTimeout(() => {
                alert(`${data.gyoku_loss.winner}の勝利です！`);
            }, 100);
        } else if (data.can_promote) {
            renderBoard();
            await showPromotionDialog(pid, data.promotion_name);
            checkAndTriggerAI();
        } else {
            statusMsg.innerText = `駒が移動しました。`;
            statusMsg.style.color = "#166534";
            renderBoard();
            checkAndTriggerAI();
        }
    } catch (err: any) {
        statusMsg.innerText = "移動に失敗しました: " + err.message;
        statusMsg.style.color = "#b91c1c";
    }
}

async function executeShishiMove(pid: number, midX: number, midY: number, toX: number, toY: number) {
    try {
        const pc = boardData.pieces.find((p: any) => p.pid === pid);
        if (pc) {
            lastMoveInfo = { pid, from_x: pc.x, from_y: pc.y, to_x: toX, to_y: toY, path: [{x: midX, y: midY}] };
        }

        const data = await engine.applyShishiMove({ pid, mid_x: midX, mid_y: midY, to_x: toX, to_y: toY });

        // 盤面を再取得
        await fetchBoard();
        resetSelection();

        if (data.gyoku_loss) {
            statusMsg.innerText = `玉将/太子が全滅！ ${data.gyoku_loss.winner}の勝利！`;
            statusMsg.style.color = "#dc2626";
            renderBoard();
            setTimeout(() => {
                alert(`${data.gyoku_loss.winner}の勝利です！`);
            }, 100);
        } else if (data.can_promote) {
            renderBoard();
            await showPromotionDialog(pid, data.promotion_name);
            checkAndTriggerAI();
        } else {
            statusMsg.innerText = `獅子の移動が完了しました。`;
            statusMsg.style.color = "#166534";
            renderBoard();
            checkAndTriggerAI();
        }
    } catch (err: any) {
        statusMsg.innerText = "獅子の移動に失敗しました: " + err.message;
        statusMsg.style.color = "#b91c1c";
    }
}

async function executeHonjuuMove(pid: number, toX: number, toY: number, visited: any[]) {
    try {
        const pc = boardData.pieces.find((p: any) => p.pid === pid);
        if (pc) {
            lastMoveInfo = { pid, from_x: pc.x, from_y: pc.y, to_x: toX, to_y: toY, path: visited.slice(0, visited.length - 1) }; // exclude destination from path if included
        }

        const data = await engine.applyHonjuuMove({ pid, to_x: toX, to_y: toY, visited });
        await fetchBoard();
        resetSelection();

        if (data.gyoku_loss) {
            statusMsg.innerText = `玉将/太子が全滅！ ${data.gyoku_loss.winner}の勝利！`;
            statusMsg.style.color = "#dc2626";
            renderBoard();
            setTimeout(() => {
                alert(`${data.gyoku_loss.winner}の勝利です！`);
            }, 100);
        } else if (data.can_promote) {
            renderBoard();
            await showPromotionDialog(pid, data.promotion_name);
            checkAndTriggerAI();
        } else {
            statusMsg.innerText = `奔鷲の移動が完了しました。`;
            statusMsg.style.color = "#166534";
            renderBoard();
            checkAndTriggerAI();
        }
    } catch (err: any) {
        statusMsg.innerText = "奔鷲の移動に失敗しました: " + err.message;
        statusMsg.style.color = "#b91c1c";
    }
}

// =========================================================================
// Promotion Dialog
// =========================================================================
function showPromotionDialog(pid: number, promotionName: string) {
    return new Promise<void>((resolve) => {
        const overlay = document.getElementById('promotion-overlay')!;
        const info = document.getElementById('promotion-info')!;
        const btnYes = document.getElementById('btn-promote-yes')!;
        const btnNo = document.getElementById('btn-promote-no')!;

        // 現在の駒名を取得
        const piece = boardData.pieces.find((p: any) => p.pid === pid);
        const currentName = piece ? piece.name : '?';
        info.innerHTML = `${formatPieceNameHTML(currentName)} → ${formatPieceNameHTML(promotionName)}`;

        overlay.style.display = 'flex';

        const cleanup = () => {
            overlay.style.display = 'none';
            btnYes.removeEventListener('click', onYes);
            btnNo.removeEventListener('click', onNo);
        };

        const onYes = async () => {
            cleanup();
            try {
                await engine.promotePiece(pid);
                await fetchBoard();
                statusMsg.innerHTML = `${formatPieceNameHTML(currentName)}が${formatPieceNameHTML(promotionName)}に成りました！`;
                statusMsg.style.color = "#166534";
                renderBoard();
            } catch (err: any) {
                statusMsg.innerText = "成りに失敗しました: " + err.message;
                statusMsg.style.color = "#b91c1c";
            }
            resolve();
        };

        const onNo = () => {
            cleanup();
            statusMsg.innerText = `成りませんでした。`;
            statusMsg.style.color = "#166534";
            resolve();
        };

        btnYes.addEventListener('click', onYes);
        btnNo.addEventListener('click', onNo);
    });
}

// =========================================================================
// Piece Search
// =========================================================================
function setupPieceSearch() {
    const searchInput = document.getElementById('piece-search') as HTMLInputElement;
    const searchDropdown = document.getElementById('search-dropdown') as HTMLElement;
    if (!searchInput || !searchDropdown) return;

    searchInput.addEventListener('input', () => {
        const q = searchInput.value.trim();
        if (!q || !boardData) {
            searchDropdown.classList.remove('visible');
            highlightedPids = [];
            renderBoard();
            return;
        }

        // name or reading 前方一致 + 部分一致
        const matches = boardData.pieces.filter((p: any) =>
            p.name.includes(q) || (p.reading && p.reading.includes(q))
        );

        // type_id でグルーピングして重複表示を抑制
        const grouped = new Map<number, any[]>();
        matches.forEach((p: any) => {
            const key = p.type_id;
            if (!grouped.has(key)) grouped.set(key, []);
            grouped.get(key)!.push(p);
        });

        // ドロップダウンを生成
        searchDropdown.innerHTML = '';
        if (grouped.size === 0) {
            searchDropdown.innerHTML = '<div class="search-item" style="color:#a8a29e;cursor:default">該当なし</div>';
            searchDropdown.classList.add('visible');
            highlightedPids = [];
            renderBoard();
            return;
        }

        grouped.forEach((pieces, typeId) => {
            const p0 = pieces[0];
            const sideLabels = pieces.map((p: any) => {
                const s = p.side === 0 ? '先' : '後';
                return `<span class="${p.side === 0 ? 'side-sente' : 'side-gote'}">${s}(${p.x},${p.y})</span>`;
            }).join(' ');

            const item = document.createElement('div');
            item.className = 'search-item';
            item.innerHTML = `
                <span>
                    <span class="piece-label">${formatPieceNameHTML(p0.name)}</span>
                    <span class="piece-reading">${p0.reading || ''}</span>
                </span>
                <span class="piece-pos">${sideLabels}</span>
            `;
            item.addEventListener('click', () => {
                highlightedPids = pieces.map((p: any) => p.pid);
                searchDropdown.classList.remove('visible');
                renderBoard();
                scrollToPiece(pieces[0]);
                if (statusMsg) {
                    statusMsg.innerHTML = `「${formatPieceNameHTML(p0.name)}」を ${pieces.length} 個ハイライト中`;
                    statusMsg.style.color = '#b45309';
                }
            });
            searchDropdown.appendChild(item);
        });

        searchDropdown.classList.add('visible');

        // 全一致を即ハイライト
        highlightedPids = matches.map((p: any) => p.pid);
        renderBoard();
    });

    // 検索欄外クリックでドロップダウンを閉じる
    document.addEventListener('click', (e: any) => {
        if (!e.target.closest('.search-wrapper')) {
            searchDropdown.classList.remove('visible');
        }
    });

    // Escapeでハイライト解除
    searchInput.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            searchInput.value = '';
            searchDropdown.classList.remove('visible');
            highlightedPids = [];
            renderBoard();
            searchInput.blur();
        }
    });
}

function scrollToPiece(piece: any) {
    const container = document.querySelector('.board-container');
    if (!container) return;
    const isFlipped = (document.getElementById('cb-flip-board') as HTMLInputElement)?.checked;
    const rx = isFlipped ? BOARD_SIZE - 1 - piece.x : piece.x;
    const ry = isFlipped ? BOARD_SIZE - 1 - piece.y : piece.y;

    const pixelX = MARGIN + rx * CELL_SIZE + CELL_SIZE / 2;
    const pixelY = MARGIN + ry * CELL_SIZE + CELL_SIZE / 2;
    // コンテナの表示領域の中央に駒が来るようスクロール
    container.scrollTo({
        left: pixelX - container.clientWidth / 2,
        top: pixelY - container.clientHeight / 2,
        behavior: 'smooth',
    });
}

// =========================================================================
// Boot
// =========================================================================

window.onload = init;
